import {
  Text,
  VStack,
  Stack,
  Flex,
  Grid,
  Button,
  ResponsiveValue,
} from '@chakra-ui/react'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'

import ScrollLink from '@/lib/scroll'

import { GET_QUOTATION } from '@/routes/landing'
import { DATASHEET_FORM } from '@/routes/resources'

import { NAVIGATION_BAR_HEIGHT } from '@/components/NavigationBar/constants'

export interface HeroProps {
  title: React.ReactNode
  subtitle: React.ReactNode
  image: ResponsiveValue<string>
  colorSchemePrimary?: string
  colorSchemeSecondary?: string
  titleColor?: string
  subtitleColor?: string
  bgColor?: string
  onClickGetQuote?: () => void
  onClickGetHandout?: () => void
  primaryButtonLink?: any
  secondaryButtonLink?: string
  primaryButtonLabel?: string
  secondaryButtonLabel?: string
}

function Hero({
  title,
  subtitle,
  image,
  titleColor,
  subtitleColor,
  colorSchemePrimary,
  colorSchemeSecondary,
  bgColor,
  onClickGetQuote,
  onClickGetHandout,
  primaryButtonLink,
  secondaryButtonLink,
  primaryButtonLabel,
  secondaryButtonLabel,
}: HeroProps): React.ReactElement {
  const { t } = useTranslation('services')

  const primaryLink = primaryButtonLink || { ...GET_QUOTATION, pathname: 'currentPath' }
  const secondaryLink = secondaryButtonLink || DATASHEET_FORM

  const primaryLabel = primaryButtonLabel || t('common.cta.getQuotation')
  const secondaryLabel = secondaryButtonLabel || t('common.cta.getHandout')

  return (
    <Flex
      as="section"
      id="hero"
      sx={{
        w: 'full',
        minH: {
          base: `calc(100vh - ${NAVIGATION_BAR_HEIGHT.base})`,
          md: `calc(100vh - ${NAVIGATION_BAR_HEIGHT.md})`,
        },
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: bgColor,
        backgroundImage: image,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        position: 'relative',
        mixBlendMode: 'multiply',
        backgroundSize: {
          base: 'cover',
          md: 'auto 100%',
        },
      }}
    >
      <Grid
        layerStyle="container"
        sx={{
          gridTemplateColumns: {
            base: '1fr',
            md: 'repeat(2, 1fr)',
          },
          gridGap: {
            base: 6,
            md: 0,
          },
          alignItems: 'center',
        }}
      >
        <VStack
          spacing="4"
          sx={{
            fontWeight: 'medium',
            alignItems: {
              base: 'center',
              md: 'flex-start',
            },
            color: 'white',
            textAlign: {
              base: 'center',
              md: 'left',
            },
            gridColumn: {
              base: '1',
              md: '2',
            },
          }}
        >
          <Text
            as="h1"
            sx={{
              color: titleColor,
              fontWeight: 'bold',
              fontSize: {
                base: '3xl',
                sm: '5xl',
                md: '5.5xl',
              },
              lineHeight: {
                base: '50px',
                sm: '68px',
                md: '72px',
              },
            }}
          >
            {title}
          </Text>
          <Text
            as="h2"
            sx={{
              color: subtitleColor,
              fontSize: {
                base: 'xl',
                sm: '3.5xl',
              },
            }}
          >
            {subtitle}
          </Text>
          <Stack
            spacing="4"
            direction={{
              base: 'column',
              md: 'row',
            }}
            sx={{
              pt: {
                base: 8,
                sm: 12,
              },
            }}
          >
            <Button
              as={ScrollLink}
              url={primaryLink}
              onClick={onClickGetQuote}
              colorScheme={colorSchemePrimary}
              size="xl"
              sx={{
                cursor: 'pointer',
              }}
            >
              {primaryLabel}
            </Button>
            <Link href={secondaryLink} passHref>
              <Button
                as="a"
                onClick={onClickGetHandout}
                layerStyle="actionable"
                colorScheme={colorSchemeSecondary}
                variant="outline"
                size="xl"
                sx={{
                  cursor: 'pointer',
                  borderWidth: '2px',
                }}
              >
                {secondaryLabel}
              </Button>
            </Link>
          </Stack>
        </VStack>
      </Grid>
    </Flex>
  )
}

export default Hero
